/* Gift Theory — Infinite scroll for the collection product grid.
   Replaces Dawn's numeric pagination with IntersectionObserver-based
   auto-fetch + append. Falls back to visible pagination if JS is disabled
   (pagination is hidden via CSS only when this script attaches).

   Strategy:
   - Read the "next page" URL from the rendered pagination
   - When a sentinel near the bottom of the grid enters the viewport,
     fetch that URL, parse it, extract new <li class="grid__item"> nodes,
     append them, and read the next URL from the response.
   - Stop when no next URL is present.
*/

(function () {
  'use strict';

  function init() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    const pagination = document.querySelector('.pagination-wrapper');
    let nextUrl = readNextUrl(document);

    // Always show the loader/sentinel, even if no next URL — so we can rule out
    // a server-side glitch by reloading and seeing the sentinel still present.
    const sentinel = document.createElement('div');
    sentinel.className = 'gt-infinite-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.innerHTML =
      '<span class="gt-infinite-loader" aria-hidden="true"></span>';

    if (pagination && pagination.parentNode) {
      pagination.parentNode.insertBefore(sentinel, pagination);
      // Hide the visible pagination — JS is enabled, infinite scroll is on
      pagination.style.display = 'none';
    } else {
      const parent = grid.parentNode || document.body;
      parent.appendChild(sentinel);
    }

    if (!nextUrl) {
      // Nothing more to load; remove the loader to avoid a perpetual spinner
      sentinel.remove();
      return;
    }

    let loading = false;

    const io = new IntersectionObserver(
      async (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting || loading || !nextUrl) return;

        loading = true;
        sentinel.classList.add('is-loading');

        try {
          const res = await fetch(nextUrl, { credentials: 'same-origin' });
          if (!res.ok) throw new Error('Fetch failed: ' + res.status);
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');

          const newItems = doc.querySelectorAll('#product-grid > li');
          newItems.forEach((item) => {
            grid.appendChild(document.importNode(item, true));
          });

          nextUrl = readNextUrl(doc);

          // Reinitialise any Shopify component-upgrade hooks on the new nodes
          // (Dawn uses custom elements; importNode preserves them automatically
          // but their connectedCallback fires on insert which is what we want.)

          if (!nextUrl) {
            sentinel.remove();
            io.disconnect();
          }
        } catch (err) {
          // Soft-fail: restore pagination so the user has a way forward
          // eslint-disable-next-line no-console
          console.warn('[gt-infinite-scroll] fetch failed, restoring pagination', err);
          if (pagination) pagination.style.display = '';
          sentinel.remove();
          io.disconnect();
        } finally {
          loading = false;
          sentinel.classList.remove('is-loading');
        }
      },
      { rootMargin: '600px 0px 600px 0px' }
    );

    io.observe(sentinel);
  }

  function readNextUrl(root) {
    // Find the highest-numbered page link in pagination — that's the "next" target.
    // (Locale-agnostic: BG, EN, etc. all label aria-label differently, so we
    // parse the page= query param directly.)
    const wrapper = root.querySelector('.pagination-wrapper');
    if (!wrapper) return null;
    const currentPage = parseInt(wrapper.getAttribute('data-page') || '1', 10);
    const links = wrapper.querySelectorAll('a[href*="page="]');
    let nextHref = null;
    for (const a of links) {
      const m = a.getAttribute('href').match(/[?&]page=(\d+)/);
      if (!m) continue;
      const n = parseInt(m[1], 10);
      if (n > currentPage) {
        // Take the first link that targets a page beyond the current one
        nextHref = a.getAttribute('href');
        break;
      }
    }
    return nextHref;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
